/**
 * Nabídkové adresy katalogu (vize struktura-webu B1–B6) — JEDINÉ místo, které ví,
 * co patří do CESTY a co do PARAMETRU.
 *
 * Nabídka má tři tvary nad jedním jmenným prostorem:
 *   `zajezdy/<destinace>`, `zajezdy/<typ>`, `zajezdy/<destinace>/<typ>`
 * a vedle nich pořád funguje parametrické zúžení (parametr destinace nad holým výpisem),
 * které na cestovní tvar jen kanonicky ukazuje. Cesta i parametr destinace přitom píšou do
 * TÉŽE dimenze filtru, takže kdyby si každá afordance (taby typu, panel filtrů, menu,
 * hledač, mazání mezipaměti) skládala adresu po svém, `zajezdy/vietnam` s otevřeným
 * panelem by tiše zúžil výsledek dvakrát — nebo naopak zahodil zúžení z cesty.
 * Proto tady, a proto v `packages/shared`: čte to web i API (sitemapa, purge).
 *
 * Vztah k sousedním kontraktům:
 * - CESTA se skládá výhradně `routes.zajezdyNabidka()` (pravidlo CLAUDE.md),
 * - PARAMETRY výhradně serializátorem kontraktu filtrů (`tripFilters.ts`), tedy VNITŘNÍMI
 *   (anglickými) názvy; na webové (české) je na výstupu překládá slovník `webParams.ts`
 *   (řez 02 vize podnety-tymu-2026-10, `internalParamsToWeb`),
 * - ZNĚNÍ nadpisů a popisů výhradně z `czLabels.ts` (guard `tripTypeNamingGuard`).
 *
 * Konvence hrany: vstup stavitelů (`source`) je VNITŘNÍ (výstup `serverUrl` na serveru,
 * `internalParamsFromLocation` v prohlížeči), výstup WEBOVÝ (`webHref`). Webová adresa na vstupu by
 * webový název filtru nechala jako „ostatní" parametr a vedle něj zapsala nový, tedy
 * dvakrát týž filtr.
 */
import { TRIP_TYPE_HEADINGS_CZ, TRIP_TYPE_META_DESCRIPTIONS_CZ } from "./czLabels";
import {
  CONTENT_PAGE_PATH_SEGMENT,
  OFFER_TYPE_PATH_SLUGS,
  PATH_SAFE_SLUG,
  parseOfferSegments,
  routes,
} from "./routes";
import { siteConfig } from "./siteConfig";
import { writeFilterSearchParams, type TripFilterState } from "./tripFilters";
import { TRIP_TYPES, type TripType } from "./types";
import { webHref } from "./webParams";

/**
 * Kanonický slug typu v cestě. Hodnoty žijí v route mapě (je to veřejná adresa),
 * tenhle alias je jen typová kontrola úplnosti: nový `TripType` bez slugu shodí
 * typecheck. Znění hlídá `tripTypeNamingGuard.test.ts`.
 */
export const TRIP_TYPE_PATH_SLUGS: Record<TripType, string> = OFFER_TYPE_PATH_SLUGS;

/** Opačná mapa (slug v cestě → typ). Odvozená, aby se nemohla rozejít. */
const TRIP_TYPE_BY_PATH_SLUG: ReadonlyMap<string, TripType> = new Map(
  TRIP_TYPES.map((type) => [TRIP_TYPE_PATH_SLUGS[type], type]),
);

/**
 * Podsegmenty stránky destinace. Lokalita s takovým slugem by se schovala za vlastní
 * podstránku své země: `/destinace/vietnam/tym` obslouží stránka týmu (statický segment
 * má v Astru přednost před dynamickým), takže lokalita `tym` by z vnořené adresy byla
 * trvale nedostupná.
 */
const DESTINATION_SUBPAGE_SLUGS: readonly string[] = ["tym"];

/**
 * Slugy, které si destinace nesmí vzít. Tři různé důvody, jedna množina:
 *  - slug TYPU zájezdu — `zajezdy/pobytove` by znamenalo dvě různé stránky a jedna
 *    by zmizela (vize B2),
 *  - podsegment stránky destinace — kolize s vnořenou adresou lokality (viz výš),
 *  - segment jednoduchých stránek (`routes.stranka`, vize F12.2) — destinace `stranka`
 *    by nesla jméno, které na webu znamená jiný druh stránky, a případná kořenová
 *    routa by z něj udělala dvě stránky pod jednou adresou.
 *
 * Vynucuje to Zod schéma destinace (`destinationCreateSchema.slug`), ne jen konvence.
 */
export const RESERVED_DESTINATION_SLUGS: readonly string[] = [
  ...Object.values(TRIP_TYPE_PATH_SLUGS),
  ...DESTINATION_SUBPAGE_SLUGS,
  CONTENT_PAGE_PATH_SEGMENT,
];

const RESERVED_SET: ReadonlySet<string> = new Set(RESERVED_DESTINATION_SLUGS);

/** Normalizuje stejně, jako by to udělal admin formulář, ať kolizi neobejde mezera. */
export function isReservedDestinationSlug(slug: string | null | undefined): boolean {
  return RESERVED_SET.has((slug ?? "").trim().toLowerCase());
}

/**
 * Tvar slugu, který smí do CESTY nabídkové adresy — týž, jaký na destinaci vynucuje
 * `destinationCreateSchema.slug`.
 *
 * Není to opis validace, je to JEJÍ NEZÁVISLÁ POJISTKA na výstupu. Do stavu filtrů se
 * slug dostává jednak z databáze (kde regex platí), jednak z adresy — a zúžení proti
 * číselníku (`knownDestinationSlugs` na webu) má schválně únikovou větev: při výpadku
 * `/api/destinations` propouští hodnotu z adresy nezměněnou, aby se z poruchy nestalo
 * tiché rozšíření nabídky. Tou dírou prošla destinace `a%0d%0ab` z parametru až sem,
 * a protože jediná destinace jde do CESTY, vyrobila (dokud výpis zaniklé časové parametry
 * přesměrovával) `Location: /zajezdy/a<CRLF>b` — hodnotu, kterou konstruktor `Headers`
 * odmítne výjimkou, tedy 500 na veřejné stránce z upravené adresy. Táž cesta by dnes
 * skončila v odkazu. Slabší varianta téhož: neznámý slug v cestě je 404 tam, kde
 * v parametru neškodí.
 *
 * Neslugový tvar se proto NEZAHAZUJE (to by nabídku rozšířilo), jen zůstane v
 * PARAMETRU, kde ho `URLSearchParams` procentně zakóduje.
 *
 * ROZSAH: platí pro adresy stavěné ze stavu filtrů, tedy pro `offerUrl` a kanonickou
 * adresu — ty jediné mají kam hodnotu odsunout. `offerPath`/`offerLinkPath` vrací holou
 * cestu, takže by neslugovou hodnotu musely zahodit (rozšíření nabídky); ty proto dál
 * důvěřují slugu z databáze, kde týž tvar vynucuje `destinationCreateSchema.slug`.
 *
 * Samotný regex bydlí v route mapě — čte ho i `parseOfferSegments`, tedy pravidlo tvaru
 * nabídkové adresy, a dvě kopie by znamenaly, že jedna z nich časem pustí, co druhá 404uje.
 */

/**
 * Destinace, která smí do CESTY: právě jedna zvolená, slugového tvaru A nerezervovaná.
 *
 * Jediná kopie pro obě adresy, které destinaci do cesty dávají (`offerUrl` i kanonická),
 * aby se nemohly rozejít v tom, kdy je adresa cestovní a kdy parametrická.
 *
 * Rezervovaný slug je táž past z druhé strany a `PATH_SAFE_SLUG` na něj nestačí:
 * `pobytove` je platný slug, jenže jako JEDINÝ segment ho `parseOfferSegments` přečte
 * jako TYP. Při výpadku číselníku (kdy zúžení hodnotu z adresy propouští) by destinace
 * `pobytove` z parametru skončila v odkazu jako `zajezdy/pobytove`, tedy tichý
 * `tripType=resort` u zákazníka, který o typ nežádal.
 */
function pathDestinationSegment(state: TripFilterState): string | null {
  const single = state.destination.length === 1 ? state.destination[0] : null;
  if (!single) return null;
  return PATH_SAFE_SLUG.test(single) && !isReservedDestinationSlug(single) ? single : null;
}

/** Co z nabídkové adresy vyčetla cesta. `null` znamená „tenhle tvar neexistuje". */
export interface OfferPathSelection {
  destinationSlug: string | null;
  tripType: TripType | null;
}

/**
 * Segmenty za `/zajezdy` → výběr, nebo `null` (volající odpoví 404, nikdy redirect
 * ani prázdný výpis — vize J2).
 *
 * TVAR adresy rozhoduje `parseOfferSegments` v route mapě — sdílí ho validace
 * admin-editovatelných odkazů (`isKnownPublicPath`), která do tohohle modulu sáhnout
 * nemůže (cyklus). Tady se výsledek jen překládá na `TripType`.
 */
export function parseOfferPath(segments: readonly string[]): OfferPathSelection | null {
  const parsed = parseOfferSegments(segments);
  if (!parsed) return null;

  return {
    destinationSlug: parsed.destination,
    tripType: parsed.typeSlug ? (TRIP_TYPE_BY_PATH_SLUG.get(parsed.typeSlug) ?? null) : null,
  };
}

/** Nabídková cesta bez parametrů. Cestu samotnou zná jen route mapa. */
export function offerPath(
  segments: { destination?: string | null; tripType?: TripType | null } = {},
): string {
  return routes.zajezdyNabidka({
    destination: segments.destination ?? null,
    typeSlug: segments.tripType ? TRIP_TYPE_PATH_SLUGS[segments.tripType] : null,
  });
}

/**
 * Nabídková cesta pro INTERNÍ ODKAZ = kanonický cíl, ne jeho kopie (vize B3).
 *
 * Pobytové BEZ destinace se kanonicky slévá do `/hotely` (`zajezdy/pobytove` nese
 * `<link rel="canonical">` právě tam), takže tam odkaz míří rovnou — odkazovat interně
 * na neindexovanou kopii je přesně to, čemu se kanonizace vyhýbá. Pobytové SE zemí má
 * vlastní význam i nadpis, a jde tedy na svou vlastní cestu.
 *
 * Volací místa ji volají v OBOU větvích (se zemí i bez ní): kdyby si podmínku držel
 * volající, rozleze se pravidlo B3 do afordancí a rozejde se s `canonicalOfferPath`.
 *
 * Rozdíl proti sousedům: `offerPath` = adresa kombinace, `offerLinkPath` = kam má vést
 * odkaz, `offerUrl` = adresa kombinace i s dědenými parametry (přepínač typů na výpisu).
 */
export function offerLinkPath(segments: {
  destination?: string | null;
  tripType?: TripType | null;
}): string {
  const destination = segments.destination ?? null;
  const tripType = segments.tripType ?? null;
  if (!destination && tripType === "resort") return routes.hotely();
  return offerPath({ destination, tripType });
}

/**
 * Stav filtrů → veřejná adresa nabídky (`cesta?parametry`).
 *
 * Destinace jde do cesty, když je v poli `destination` PRÁVĚ JEDNA hodnota a smí do
 * segmentu (`pathDestinationSegment` — slugový tvar, nerezervovaný slug; podmínky
 * a jejich důvody viz tam). Typ jde do cesty vždy, když je zvolený. Co se dostalo do
 * cesty, se do parametrů už nezapíše — jinak by se dimenze zúžila dvakrát.
 *
 * `source` je VNITŘNÍ query string (výstup `serverUrl`, v prohlížeči
 * `internalParamsFromLocation`), výstup je WEBOVÝ: celý query string projde na konci
 * `webHref` nad výslednou cestou. Průchozí parametry (`utm_*`) slovník nemění.
 *
 * Query string se dědí CELÝ, ne výběrově: `source` (aktuální URL) se jen zbaví jmenného
 * prostoru filtrů, čísla stránky a mrtvého `filterOpen`, zbytek projde beze změny. Není
 * to kosmetika — UTM parametry sdíleného odkazu v kontraktu filtrů nejsou, takže
 * stavitel dědící jen vyjmenované `sort`/`view` by po každém kliku na typ zahodil zdroj
 * návštěvy.
 *
 * `filterOpen` je naopak jediný parametr MIMO kontrakt, který se dědit nesmí: do řezu 05
 * držel otevřený desktopový panel, jenže ten je od té doby otevřený ve výchozím stavu
 * a jeho skrytí žije u návštěvníka (`localStorage`), ne v adrese (vize D2). Kdyby se dědil,
 * rozvlekl by ho příchozí sdílený odkaz do všech dalších adres, které stránka vyrobí.
 */
export function offerUrl(state: TripFilterState, source?: URLSearchParams): string {
  const destinationInPath = pathDestinationSegment(state);
  const path = offerPath({ destination: destinationInPath, tripType: state.tripType });

  // Kopie, ne mutace vstupu — volající (`Astro.url.searchParams`) s ním pracuje dál.
  const params = new URLSearchParams(source ? source.toString() : "");
  // Předřazený `clearFilterSearchParams` tu NENÍ schválně: `writeFilterSearchParams`
  // každý klíč kontraktu buď nastaví, nebo smaže (a legacy klíče maže taky), takže
  // by nic neuklidil. Vlastnost, na které to stojí, vynucuje `tripFiltersGuard`.
  params.delete("page");
  params.delete("filterOpen");
  writeFilterSearchParams(params, {
    ...state,
    destination: destinationInPath ? [] : state.destination,
    tripType: null,
  });

  return webHref(path, params);
}

/**
 * Kanonická adresa daného stavu (vize B3/B4/B5). Oproti `offerUrl` zahazuje VŠECHNO
 * ostatní — ostatní filtry, řazení, pohled i stránku — aby se týž obsah v indexu
 * nemnožil pod desítkami adres. Druhá stránka stránkování tedy kanonicky ukazuje na
 * první; u odfiltrovaných výpisů je to obvyklá praxe a mění se právě tady.
 */
export function canonicalOfferPath(state: TripFilterState): string {
  const single = canonicalDestinationSegment(state);

  // B3: pobytové zájezdy BEZ destinace mají kanonický domov ve výloze `/hotely`.
  // Rozhoduje o tom `offerLinkPath` — týž pojem musí mít jeden cíl, ať ho staví
  // `<link rel="canonical">` nebo interní odkaz (druhá kopie pravidla by se při
  // příští změně B3 rozešla).
  // Podmínka je „nula destinací", ne „ne právě jedna": při dvou a víc zemích je
  // `single` taky `null`, ale ta stránka je vlastní množina (pobytové ve Vietnamu
  // a Thajsku), kterou `/hotely` neukazuje — kanonizovat ji tam znamená poslat
  // sdílený odkaz i robota na cizí obsah.
  if (state.destination.length === 0) {
    return offerLinkPath({ destination: null, tripType: state.tripType });
  }

  const path = offerPath({ destination: single, tripType: state.tripType });
  if (single) return path;

  const params = new URLSearchParams();
  params.set("destination", state.destination.join(","));
  return webHref(path, params);
}

/**
 * Slug destinace, který `canonicalOfferPath` dá do CESTY (`null` = žádný).
 *
 * Existuje kvůli politice indexace: destinační nabídkové stránky nesou do dalšího řezu
 * `noindex, follow` (vize C2) a ten příznak se musí vázat na KANONICKOU adresu, ne na
 * tvar té právě navštívené. Jinak parametrické zúžení na Vietnam řekne robotovi
 * „indexuj mě" a zároveň ho kanonicky pošle na `zajezdy/vietnam`, která říká
 * „neindexuj" — na takový shluk vyhledávač obvykle reaguje vyřazením obojího.
 */
export function canonicalDestinationSegment(state: TripFilterState): string | null {
  return pathDestinationSegment(state);
}

export interface OfferPageMeta {
  /** Nadpis první úrovně výpisu. */
  heading: string;
  /** `<title>` (už se značkou — `BaseLayout` ji podruhé nepřidává). */
  title: string;
  /** `<meta name="description">`; ořez na SEO délku patří webové vrstvě. */
  description: string;
}

/** Výchozí (katalogový) popis — holý `/zajezdy` bez destinace i bez typu. */
const CATALOG_DESCRIPTION =
  "Prohlédněte si katalog zájezdů CK Go2 — poznávací s průvodcem, privátní na míru i pobytové u moře. Filtrujte podle destinace, termínu nebo ceny.";

/**
 * Nadpis, titulek a popis nabídkové stránky — JEDINÉ místo, kde se skládají, aby
 * se daly otestovat bez renderování `.astro` a aby se cestovní a parametrický tvar
 * nemohly rozejít ve znění.
 */
export function offerPageMeta(input: {
  destinationName?: string | null;
  tripType?: TripType | null;
}): OfferPageMeta {
  const name = (input.destinationName ?? "").trim();
  const tripType = input.tripType ?? null;

  const heading = tripType
    ? name
      ? `${TRIP_TYPE_HEADINGS_CZ[tripType]} – ${name}`
      : TRIP_TYPE_HEADINGS_CZ[tripType]
    : name
      ? `Zájezdy ${name}`
      : "Všechny zájezdy";

  // Katalogový výchozí stav má vlastní titulek: „Všechny zájezdy" je nadpis stránky,
  // ale v záložce prohlížeče i v SERPu je srozumitelnější „Katalog zájezdů".
  const titleBase = !tripType && !name ? "Katalog zájezdů" : heading;

  const description = tripType
    ? name
      ? `${TRIP_TYPE_HEADINGS_CZ[tripType]} – ${name}. Vyberte si termín, cenu i délku, nebo si nechte nabídku sestavit přesně podle sebe.`
      : TRIP_TYPE_META_DESCRIPTIONS_CZ[tripType]
    : name
      ? `Zájezdy ${name} — poznávací s průvodcem, privátní na míru i pobytové u moře. Vyberte si termín, délku i cenu, kterou hledáte.`
      : CATALOG_DESCRIPTION;

  return { heading, title: `${titleBase} | ${siteConfig.brand.name}`, description };
}
